import { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { TrussEnvelope } from '@paragon/contract-library';

import { useAppSelector } from '../../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../../library/layout-geometry/coordinates';
import { RootState } from '../../../store';
import { getVisibleTrussEnvelopes } from '../../../store/selectors/layoutDataSelectors';
import { isPickingActive } from '../../../store/selectors/pickingSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../../store/selectors/trussEnvelopeInsertionInformationSelectors';
import { useMousePositionInScene } from '../../hooks/useMousePositionInScene';

const getTrussEnvelopesForRoofContainer = createSelector(
  getVisibleTrussEnvelopes,
  (state: RootState, roofContainerGuid: string) => roofContainerGuid,

  (trussEnvelopes: TrussEnvelope[], roofContainerGuid: string) =>
    trussEnvelopes.filter((envelope) => envelope.roofContainerGuid === roofContainerGuid),
);

export function useTrussEnvelopesWithInsertionEnvelope(roofContainerGuid?: string) {
  const insertionInformation = useAppSelector(getTrussEnvelopeInsertionInformation);
  const insertingTrussEnvelope =
    insertionInformation != null &&
    (roofContainerGuid == null ||
      (insertionInformation.hasRoofContainer() && insertionInformation.data.roofContainerGuid === roofContainerGuid));

  const trussEnvelopes = useAppSelector(
    roofContainerGuid
      ? (state) => getTrussEnvelopesForRoofContainer(state, roofContainerGuid)
      : getVisibleTrussEnvelopes,
  );

  const mousePosition = useMousePositionInScene(insertingTrussEnvelope);
  const isPicking = useAppSelector(isPickingActive);

  return useMemo(() => {
    if (insertingTrussEnvelope) {
      if (insertionInformation.isInsertionEnvelopeFullyDefined()) {
        return trussEnvelopes.concat([{ ...insertionInformation.data, name: '' }]);
      }

      // FIXME: I'm not happy with this dependency on isPicking. Figuring out when insertion is occurring is not the
      //   concern of this component.
      if (mousePosition && !isPicking) {
        const mousePosition2D = convertFromDesignToLayoutCoordinates(mousePosition).projectTo2D();
        const calculationResult =
          insertionInformation.calculateInsertionEnvelopeUsingMousePositionIfPossible(mousePosition2D);

        if (calculationResult) {
          return trussEnvelopes.concat([calculationResult.toTrussEnvelope('0', insertionInformation.data)]);
        }
      }
    }

    return trussEnvelopes;
  }, [insertingTrussEnvelope, insertionInformation, mousePosition, isPicking, trussEnvelopes]);
}
